import React, { useCallback, useMemo, useState } from 'react';
import { graphql, useFragment } from 'react-relay';
import Button from '@atlaskit/button';
import DropdownMenu, { DropdownItem, DropdownItemGroup } from '@atlaskit/dropdown-menu';
import MoreIcon from '@atlaskit/icon/glyph/more';
import ShortcutIcon from '@atlaskit/icon/glyph/shortcut';
import { AnyAri } from '@atlassian/ari/any-ari';
import { ff } from '@atlassian/jira-feature-flagging';
import {
	EntryKeys,
	ENTRYPOINT_ID_JIRA_SOFTWARE_PROJECT,
	useFeedbackCollectorActions,
} from '@atlassian/jira-feedback-collector';
import { graphProdURL, graphStgURL } from '@atlassian/jira-graph/src/ui/constants';
import { useIntl } from '@atlassian/jira-intl';
import { fireUIAnalytics, useAnalyticsEvents } from '@atlassian/jira-product-analytics-bridge';
import type { ProjectContext } from '@atlassian/jira-providers-project-context/src/types';
import type { DevOpsProviderNamespace } from '@atlassian/jira-relay/src/__generated__/main_softwareCodePage_Header.graphql';
import type { main_softwareCodePage_MoreMenu$key } from '@atlassian/jira-relay/src/__generated__/main_softwareCodePage_MoreMenu.graphql';
import { isDevopsFeatureDisabledInFedRamp } from '@atlassian/jira-software-devops-fedramp-utils';
import { useCloudId, useTenantContext } from '@atlassian/jira-tenant-context-controller';
import { FEEDBACK_COLLECTOR_LOCATION } from '../../constants';
import { isBitbucketProvider } from '../../utils';
import messages from './messages';

type Props = {
	moreMenuData: main_softwareCodePage_MoreMenu$key | null;
	projectContext: ProjectContext;
	isAdmin: boolean;
};

const Icon = <MoreIcon label="More actions" />;

const isOAuthProvider = (providerNamespace: DevOpsProviderNamespace | null | undefined): boolean =>
	providerNamespace === 'OAUTH';

export const MoreMenu = ({ projectContext, moreMenuData, isAdmin: isJiraGlobalAdmin }: Props) => {
	const [isActionMenuOpen, setIsActionMenuOpen] = useState(false);
	const { openFeedbackCollector } = useFeedbackCollectorActions();
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const { environment } = useTenantContext();
	const { formatMessage } = useIntl();
	const cloudId = useCloudId();

	const dataDepotData = useFragment(
		graphql`
			fragment main_softwareCodePage_MoreMenu on DevOpsProviders {
				devInfoProviders {
					__typename
					id @required(action: THROW)
					name
					namespace
				}
			}
		`,
		moreMenuData,
	);

	const dataDepotProviders = useMemo(
		() => dataDepotData?.devInfoProviders?.filter(Boolean) ?? [],
		[dataDepotData],
	);
	const additionalFields = useMemo(() => {
		const devInfoProviders = dataDepotData?.devInfoProviders ?? [];

		return [
			{
				id: EntryKeys.CODE_IN_JIRA_PROVIDERS,
				value: devInfoProviders
					.map((provider) => {
						const isOauthProvider = isOAuthProvider(provider?.namespace);
						return isOauthProvider ? 'UGC provider name hidden' : provider?.name ?? '';
					})
					.join(', '),
			},
			{ id: EntryKeys.CLOUD_ID, value: cloudId },
		];
	}, [dataDepotData, cloudId]);

	const buildDropdownListItems = useCallback(() => {
		const dropdownItems = [];

		const isProjectAdmin = projectContext.isProjectAdmin != null && projectContext.isProjectAdmin;

		const canSeeProjectSettings = isProjectAdmin || isJiraGlobalAdmin;

		const hasBitbucketProvider = !!dataDepotProviders.find((provider) =>
			isBitbucketProvider(provider.id),
		);

		const canSeeBitbucketAccountsLink = isJiraGlobalAdmin && hasBitbucketProvider;

		if (canSeeBitbucketAccountsLink) {
			const manageBitbucketAccountsClick = () => {
				fireUIAnalytics(
					createAnalyticsEvent({ action: 'clicked', actionSubject: 'link' }),
					'manageBitbucketAccountsClick',
				);
			};

			dropdownItems.push(
				<DropdownItem
					href="/secure/admin/ConfigureDvcsOrganizations!default.jspa"
					onClick={manageBitbucketAccountsClick}
				>
					{formatMessage(messages.manageBitbucketAccountsLink)}
				</DropdownItem>,
			);
		}

		const canManageApps = isJiraGlobalAdmin;

		if (canManageApps) {
			const classicProviders = dataDepotProviders.filter(
				(provider) => provider.namespace === 'CLASSIC',
			);

			classicProviders.forEach((provider) => {
				const providerName = provider.name;
				const shortcutIcon = <ShortcutIcon label={`${provider.name}`} size="small" />;

				const providerClick = () => {
					fireUIAnalytics(
						createAnalyticsEvent({ action: 'clicked', actionSubject: 'link' }),
						'providerClick',
						{
							providerId: provider.id,
							providerNamespace: provider.namespace,
						},
					);
				};

				dropdownItems.push(
					<DropdownItem
						href="/plugins/servlet/upm"
						target="_blank"
						elemAfter={shortcutIcon}
						onClick={providerClick}
					>
						{formatMessage(messages.thirdPartyProviderSettingsLink, {
							providerName,
						})}
					</DropdownItem>,
				);
			});
		}

		if (canSeeProjectSettings) {
			const classicProjectFeaturesLink = `/jira/software/c/projects/${projectContext.projectKey}/settings/features`;
			const nextGenProjectFeaturesLink = `/jira/software/projects/${projectContext.projectKey}/settings/features`;

			const settingsUrl = projectContext.simplified
				? nextGenProjectFeaturesLink
				: classicProjectFeaturesLink;

			const settingsClick = () => {
				fireUIAnalytics(
					createAnalyticsEvent({ action: 'clicked', actionSubject: 'link' }),
					'settingsClick',
				);
			};

			dropdownItems.push(
				<DropdownItem href={settingsUrl} onClick={settingsClick}>
					{formatMessage(messages.settingsLink)}
				</DropdownItem>,
			);
		}

		if (ff('platform.atlassian.griffin-navigation-redesign')) {
			if (!isDevopsFeatureDisabledInFedRamp() && ff('otc.show-me-the-graph')) {
				const from = AnyAri.create({
					resourceId: projectContext.projectId.toString(),
					resourceType: 'project',
					resourceOwner: 'jira',
					cloudId,
				}).toString();
				const graphURL = `${environment === 'staging' ? graphStgURL : graphProdURL}?ari=${from}`;
				dropdownItems.push(
					<DropdownItem
						href={graphURL}
						target="_blank"
						data-testid="software-code-page.common.ui.more-menu.menuitem-graph"
					>
						{formatMessage(messages.graphButtonLabelNonFinal)}
					</DropdownItem>,
				);
			}

			dropdownItems.push(
				<DropdownItem
					data-testid="software-code-page.common.ui.more-menu.menuitem-feedback"
					onClick={() =>
						openFeedbackCollector({
							entrypointId: ENTRYPOINT_ID_JIRA_SOFTWARE_PROJECT,
							feedbackGroupId: FEEDBACK_COLLECTOR_LOCATION,
							feedbackContext: additionalFields,
						})
					}
				>
					{formatMessage(messages.leaveFeedback)}
				</DropdownItem>,
			);
		}

		return dropdownItems;
	}, [
		dataDepotProviders,
		createAnalyticsEvent,
		formatMessage,
		isJiraGlobalAdmin,
		projectContext.isProjectAdmin,
		projectContext.projectKey,
		projectContext.projectId,
		projectContext.simplified,
		cloudId,
		additionalFields,
		environment,
		openFeedbackCollector,
	]);

	const ddlItems = useMemo(() => buildDropdownListItems(), [buildDropdownListItems]);

	const onOpenChange = () => {
		setIsActionMenuOpen(!isActionMenuOpen);

		const analyticsEvent = createAnalyticsEvent({
			action: 'clicked',
			actionSubject: 'button',
			attributes: {
				componentName: 'button',
				packageName: '@atlaskit/button',
				packageVersion: '16.5.2',
			},
		});

		fireUIAnalytics(analyticsEvent, 'moreBtnClicked');
	};

	return (
		<>
			{ddlItems.length > 0 && (
				<DropdownMenu
					trigger={({ triggerRef, ...props }) => (
						<Button {...props} iconAfter={Icon} appearance="subtle" ref={triggerRef} />
					)}
					placement="bottom-end"
					onOpenChange={onOpenChange}
					isOpen={isActionMenuOpen}
				>
					<DropdownItemGroup>
						{ddlItems.map((component, index) => (
							<React.Fragment key={index}>{component}</React.Fragment>
						))}
					</DropdownItemGroup>
				</DropdownMenu>
			)}
		</>
	);
};
